import { EnumButtonName } from '@calm/evexia-analytics';
import {
    Box,
    Button,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    SxProps,
    Theme,
} from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { ComponentProps, ElementType, ReactNode } from 'react';

const styleIcon = {
    justifyContent: 'end',
};

type Props = {
    divider?: boolean;
    reverseTextStyles?: boolean;
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
    primaryText: string;
    secondaryText?: string;
    disablePadding?: boolean;
    sx?: SxProps<Theme>;
    wrapperSx?: SxProps<Theme>;
} & OptionalButton;

type OptionalButton =
    | {
          href: string;
          openInNewTab?: boolean;
          component: ElementType<any>;
          trackButton: EnumButtonName;
          trackProps?: ComponentProps<typeof Button>['trackProps'];
          disabled?: boolean;
          onClick?: never;
      }
    | {
          href?: never;
          openInNewTab?: never;
          component?: never;
          trackButton?: never;
          trackProps?: never;
          onClick?: never;
          disabled?: never;
      }
    | {
          href?: never;
          openInNewTab?: never;
          component: ElementType<any>;
          trackButton: EnumButtonName;
          trackProps?: ComponentProps<typeof Button>['trackProps'];
          disabled?: boolean;
          onClick: () => void;
      };

const styleWrapperListItem = (disablePadding?: boolean) => ({
    display: 'flex',
    ...(disablePadding
        ? {}
        : {
              paddingLeft: { xs: 4, md: 8 },
              paddingRight: { xs: 4, md: 8 },
          }),
});

function WrapperListItem({
    trackButton,
    trackProps,
    children,
    onClick,
    href,
    openInNewTab,
    component,
    disablePadding,
    disabled,
    sx,
}: {
    children: ReactNode;
    disablePadding?: boolean;
    sx?: SxProps<Theme>;
} & Pick<
    OptionalButton,
    | 'href'
    | 'openInNewTab'
    | 'component'
    | 'trackButton'
    | 'onClick'
    | 'trackProps'
    | 'disabled'
>) {
    const buttonRequirements = trackButton && component;
    const buttonActions = href || onClick;
    if (!buttonRequirements || !buttonActions) {
        return <Box sx={styleWrapperListItem(disablePadding)}>{children}</Box>;
    }

    if (onClick) {
        return (
            <ListItemButton
                trackButton={trackButton}
                trackProps={trackProps}
                onClick={onClick}
                component={component}
                disabled={disabled}
                sx={sxAppend(styleWrapperListItem(disablePadding), sx)}
            >
                {children}
            </ListItemButton>
        );
    }

    return (
        <ListItemButton
            trackButton={trackButton}
            trackProps={trackProps}
            href={href}
            {...(openInNewTab ? { target: '_blank' } : {})}
            component={component}
            sx={sxAppend(styleWrapperListItem(disablePadding), sx)}
        >
            {children}
        </ListItemButton>
    );
}
const styleListItemText = {
    '.MuiListItemText-primary': {
        color: 'text.body',
    },
    '.MuiListItemText-secondary': {
        color: 'text.sub',
    },
};
const styleReverseListItemText = {
    '.MuiListItemText-primary': {
        color: 'text.sub',
    },

    '.MuiListItemText-secondary': {
        color: 'text.body',
    },
};

export default function SimpleListItem({
    divider,
    trackButton,
    trackProps,
    reverseTextStyles,
    leftIcon,
    rightIcon,
    primaryText,
    secondaryText,
    disablePadding,
    disabled,
    href,
    openInNewTab,
    onClick,
    component,
    sx,
    wrapperSx,
}: Props) {
    return (
        <ListItem
            aria-label={primaryText}
            disablePadding={disablePadding}
            divider={divider}
            sx={sx}
        >
            <WrapperListItem
                href={href}
                openInNewTab={openInNewTab}
                trackButton={trackButton}
                trackProps={trackProps}
                component={component}
                onClick={onClick}
                disabled={disabled}
                disablePadding={disablePadding}
                sx={wrapperSx}
            >
                {leftIcon && (
                    <ListItemAvatar
                        sx={{ display: 'flex', alignItems: 'center' }}
                    >
                        {leftIcon}
                    </ListItemAvatar>
                )}
                <ListItemText
                    primary={primaryText}
                    primaryTypographyProps={{
                        variant: reverseTextStyles
                            ? 'subtitle2'
                            : 'buttonLarge',
                        component: 'div',
                    }}
                    secondary={secondaryText}
                    secondaryTypographyProps={{
                        variant: reverseTextStyles
                            ? 'buttonLarge'
                            : 'subtitle2',
                        component: 'div',
                    }}
                    sx={
                        reverseTextStyles
                            ? styleReverseListItemText
                            : styleListItemText
                    }
                />
                {rightIcon && (
                    <ListItemIcon sx={styleIcon}>{rightIcon}</ListItemIcon>
                )}
            </WrapperListItem>
        </ListItem>
    );
}
